import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useIsAuthenticated } from '@azure/msal-react'

import { AuthProvider } from '@/providers/AuthProvider'
import { ThemeProvider } from '@/providers/ThemeProvider'
import { OnboardingProvider } from '@/providers/OnboardingProvider'
import { PerformanceProvider } from '@/contexts/PerformanceContext'
import { ConfirmDialogProvider } from '@/components/providers/ConfirmDialogProvider'
import { OnboardingGuard } from '@/components/guards/OnboardingGuard'
import { AdminRoute } from '@/components/auth/AdminRoute'
import { WorkforceRoute } from '@/components/auth/WorkforceRoute'
import { Layout } from '@/components/layout/Layout'
import { LoginPage } from '@/pages/auth/LoginPage'
import { AuthCallback } from '@/pages/auth/AuthCallback'
import EmployeeJourneyPage from '@/pages/performance/EmployeeJourneyPage'
import ManagerJourneyPage from '@/pages/performance/ManagerJourneyPage'
import HRAdminDashboard from '@/pages/admin/HRAdminDashboard'
import BrandShowcase from '@/components/brand/BrandCompliance'
import OnboardingPageIntegrated from '@/pages/onboarding/OnboardingPageIntegrated'
import AnonymousFeedbackResponsePage from '@/pages/feedback/AnonymousFeedbackResponsePage'
import OrganizationPage from '@/pages/organization/OrganizationPage'
import RoleMasterPage from '@/pages/role-master/RoleMasterPage'
import WorkerHistoryPage from '@/pages/history/WorkerHistoryPage'
import MyDocumentsPage from '@/pages/documents/MyDocumentsPage'
import StaffOrderPage from '@/pages/staff-order/StaffOrderPage'
import DashboardsPage from '@/pages/workforce/DashboardsPage'
import HireRequestsPage from '@/pages/hire-requests/HireRequestsPage'
import TerminationRequestsPage from '@/pages/termination-requests/TerminationRequestsPage'
import { documentsEnabled, hireRequestsEnabled, terminationRequestsEnabled } from '@/lib/featureFlags'

// Public routes that don't require authentication
const PUBLIC_ROUTES = ['/feedback/respond']

// Agentic assistant preview — dark by default. The chunk is code-split via
// React.lazy so it is never fetched unless the flag is on AND the route is
// visited. Flip VITE_ENABLE_ASSISTANT=true (e.g. in .env.local) to see it.
const ASSISTANT_ENABLED = import.meta.env.VITE_ENABLE_ASSISTANT === 'true'
const AssistantPage = lazy(() => import('@/pages/assistant/AssistantPage'))

function App() {
  const isAuthenticated = useIsAuthenticated()
  const location = useLocation()

  // NOTE: Redirect handling is done in main.tsx before App renders
  // to prevent race conditions with multiple handleRedirectPromise() calls

  // Check if current path is a public route
  const isPublicRoute = PUBLIC_ROUTES.some(route => location.pathname.startsWith(route))

  // Bypass authentication in demo mode or for public routes.
  // DEV-only BY CONSTRUCTION: demo mode exists for the local demo-recording
  // pipeline (vite dev server). Guarding on import.meta.env.DEV means a
  // production build can never carry the auth bypass, even if a stray
  // .env.local with VITE_DEMO_MODE=true leaks into the build context —
  // exactly what shipped to PROD on 2026-07-14 (login page suppressed,
  // null-user shell → "undefined undefined" header).
  const isDemoMode = import.meta.env.DEV && import.meta.env.VITE_DEMO_MODE === 'true'
  
  if (!isAuthenticated && !isDemoMode && !isPublicRoute) {
    // Save the full path including query params for post-login redirect
    // Use pathname for the callback check since the hash may contain MSAL auth codes
    // (e.g. /auth/callback#code=...) which would bypass an exact string match
    if (location.pathname !== '/' && !location.pathname.startsWith('/auth/callback')) {
      const returnUrl = `${location.pathname}${location.search}`
      // Use localStorage instead of sessionStorage to survive MSAL logout
      localStorage.setItem('returnUrl', returnUrl)
    }
    return <LoginPage />
  }

  return (
    <ThemeProvider defaultFaculty="default">
      <AuthProvider>
        <OnboardingProvider>
          <ConfirmDialogProvider>
            <PerformanceProvider>
              <Routes>
                {/* Public Routes - No Authentication Required */}
                <Route path="/feedback/respond/:token" element={<AnonymousFeedbackResponsePage />} />
                
                {/* Auth Callback - Must be OUTSIDE OnboardingGuard to process returnUrl immediately */}
                <Route path="/auth/callback" element={<AuthCallback />} />
                
                {/* Authenticated Routes */}
                <Route path="/*" element={
                  <OnboardingGuard>
                    <Layout>
                      <Routes>
                        {/* Primary Routes */}
                        <Route path="/" element={<Navigate to="/employee-journey" replace />} />
              
              {/* Onboarding */}
              <Route path="/onboarding" element={<OnboardingPageIntegrated />} />
              
              {/* Journey Routes */}
              <Route path="/employee-journey" element={<EmployeeJourneyPage />} />
              {/* Employee self-service documents — no role guard: the API scopes
                  the list to the verified caller's own record. Mounted only where
                  the build carries VITE_DOCUMENTS_ENABLED=true (the environment
                  must have the documents API). */}
              {documentsEnabled() && <Route path="/my-documents" element={<MyDocumentsPage />} />}
              {/* Staff apparel order against a personal voucher — no role guard:
                  the API scopes the voucher and the order to the verified caller,
                  and answers "nothing open for you" to everyone else. Linked from
                  the invitation email. */}
              <Route path="/staff-order" element={<StaffOrderPage />} />
              <Route path="/people-leader-hub" element={<ManagerJourneyPage />} />
              {/* Hire requests for line managers — mounted only where the build
                  carries VITE_HIRE_REQUESTS_ENABLED=true. No role guard: the API
                  decides who manages a team and answers 404 while identity's
                  switch is off. */}
              {hireRequestsEnabled() && <Route path="/hire-requests" element={<HireRequestsPage />} />}
              {/* Termination requests: a team member's resignation, or one their
                  People Leader raises — mounted only where the build carries
                  VITE_TERMINATION_REQUESTS_ENABLED=true and the documents store.
                  No role guard: the API decides who may raise one for whom and
                  answers 404 while identity's switch is off. */}
              {terminationRequestsEnabled() && <Route path="/termination-requests" element={<TerminationRequestsPage />} />}
              {/* Legacy path redirect — preserves old bookmarks/links */}
              <Route path="/manager-journey" element={<Navigate to="/people-leader-hub" replace />} />
              <Route path="/admin" element={<AdminRoute><HRAdminDashboard /></AdminRoute>} />
              <Route path="/organization" element={<AdminRoute><OrganizationPage /></AdminRoute>} />
              <Route path="/role-master" element={<AdminRoute><RoleMasterPage /></AdminRoute>} />
              <Route path="/history" element={<AdminRoute><WorkerHistoryPage /></AdminRoute>} />
              {/* Its own guard, not AdminRoute — a workforce-only grantee must NOT
                  be admitted to the rest of the admin shell. Dashboards: the Workforce
                  Explorer and the talent dashboard, each section for whoever may see it. */}
              <Route path="/workforce-explorer" element={<WorkforceRoute><DashboardsPage /></WorkforceRoute>} />

              {/* Agentic assistant preview — only mounted when the flag is on.
                  Wrap in <AdminRoute> (or a People-Leader guard) before any real rollout. */}
              {ASSISTANT_ENABLED && (
                <Route path="/assistant" element={
                  <Suspense fallback={<div className="p-8 text-sm text-gray-400">Loading assistant…</div>}>
                    <AssistantPage />
                  </Suspense>
                } />
              )}

              {/* Legacy Route Redirects */}
              <Route path="/objectives" element={<Navigate to="/employee-journey" replace />} />
              <Route path="/kpis" element={<Navigate to="/employee-journey" replace />} />
              <Route path="/strategic" element={<Navigate to="/employee-journey" replace />} />
              <Route path="/strategy" element={<Navigate to="/employee-journey" replace />} />
              <Route path="/reviews" element={<Navigate to="/employee-journey" replace />} />
              <Route path="/feedback" element={<Navigate to="/employee-journey" replace />} />
              <Route path="/development" element={<Navigate to="/employee-journey" replace />} />
              <Route path="/talent" element={<Navigate to="/people-leader-hub" replace />} />
              <Route path="/analytics" element={<Navigate to="/people-leader-hub" replace />} />
              
              {/* Utility Routes */}
              <Route path="/brand-showcase" element={<BrandShowcase />} />
              <Route path="*" element={<Navigate to="/employee-journey" replace />} />
                      </Routes>
                    </Layout>
                  </OnboardingGuard>
                } />
              </Routes>
            </PerformanceProvider>
          </ConfirmDialogProvider>
        </OnboardingProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App
